import { AuthenticatedRequest } from '../../auth/authenticated-request';

export interface ConferenceReport {
  report_id: string;
  email: string;
  client_name?: string | null;
  status: string;
  submitted_at: string;
  updated_at: string;
  status_url: string;
  coverage: Record<string, unknown>;
  limitations: string[];
  report_url: string | null;
  delivery: string | null;
  completion_target?: string | null;
  target_missed?: boolean;
  completed_late?: boolean;
  working_deadline?: string | null;
  recovery_eligible?: boolean;
  recovery_blocker?: string | null;
  recovery_needs_api_key?: boolean;
  recovery_state?: string | null;
  recovery_mode?: string | null;
  recovery_attempts?: number;
  recovery_started_at?: string | null;
}

export interface Submission {
  email: string;
  api_key: string;
}

export interface ReportResponse {
  report: ConferenceReport;
  pending: boolean;
  retryAfterMs?: number;
}

export interface ReportPage {
  items: ConferenceReport[];
  next_cursor: string | null;
}

type RequestWithAuth = <T>(request: AuthenticatedRequest<T>) => Promise<T>;
type Method = 'GET' | 'POST' | 'DELETE' | 'RESUME';
type RequestKind = Method | 'LIST';

export const REPORT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Read exposed Retry-After headers; CORS may hide them from the browser. */
export function retryAfterMs(value: string | null): number | undefined {
  if (!value?.trim()) {
    return undefined;
  }
  const seconds = Number(value);
  const delay = Number.isFinite(seconds)
    ? seconds * 1000
    : Date.parse(value) - Date.now();
  return Number.isFinite(delay) && delay >= 0
    ? Math.min(2_147_483_647, Math.max(1000, delay))
    : undefined;
}

/** Retain only safe messages, never response bodies or submitted credentials. */
export class ConferenceApiError extends Error {
  constructor(
    public status: number,
    method: RequestKind,
    public retryAfterMs?: number,
  ) {
    const messages: Record<number, string> = {
      401: 'Your staff session expired. Sign in again, then retry.',
      403: 'HouseWhisper staff access is required. Check your signed-in account and the allowed browser origin.',
      404: 'This report was not found or has already been deleted.',
      409:
        method === 'DELETE'
          ? 'Report recovery or email delivery is in progress. The report has not been deleted. Retry deletion later.'
          : method === 'RESUME'
            ? 'Report ownership or delivery changed. Wait before retrying recovery.'
            : 'Submission ownership changed. Wait before submitting again.',
      413:
        method === 'RESUME'
          ? 'The recovery request is too large. Check the replacement API key length.'
          : 'The submission is too large. Check the email and API key lengths.',
      422:
        method === 'POST'
          ? 'Check the customer email and FUB API key. FUB identity could not be validated.'
          : method === 'RESUME'
            ? 'The recovery request is invalid. Check the report ID and replacement API key.'
            : 'The report identifier or request is invalid.',
      503: 'The conference report service is temporarily unavailable.',
    };
    const action =
      method === 'POST'
        ? 'Submission was not acknowledged. Retry manually; an existing account report will be reused.'
        : method === 'RESUME'
          ? 'Recovery was not confirmed. Keep this report ID, check its status, and retry recovery manually.'
          : method === 'DELETE'
            ? 'Stopping or deletion was not confirmed. Keep this report and retry manually.'
            : method === 'LIST'
              ? 'The report list could not be refreshed. Retry with Refresh.'
              : [401, 403, 404, 413, 422].includes(status)
                ? 'Your report ID is retained. Correct the issue, then check again.'
                : 'Status could not be checked. Your report ID is retained; status checks will retry.';
    const message =
      method === 'LIST' && [404, 405, 501].includes(status)
        ? 'Report runs are unavailable. The backend must provide the GET /reports collection endpoint.'
        : method === 'LIST' && status >= 200 && status < 300
          ? 'The backend returned an unsupported response. GET /reports must return items and next_cursor for the report list.'
          : messages[status] ||
            'Could not reach the conference report service.';
    super(`${message} ${action}`);
    this.name = 'ConferenceApiError';
  }
}

/** Whitelist the public status contract, including legacy non-success statuses. */
function parseReport(body: unknown): ConferenceReport | undefined {
  if (!body || typeof body !== 'object') {
    return undefined;
  }
  const value = body as Record<string, unknown>;
  if (
    typeof value.report_id !== 'string' ||
    !REPORT_ID_PATTERN.test(value.report_id) ||
    !['email', 'status', 'submitted_at', 'updated_at', 'status_url'].every(
      field => typeof value[field] === 'string',
    )
  ) {
    return undefined;
  }
  return {
    report_id: value.report_id,
    email: value.email as string,
    client_name:
      typeof value.client_name === 'string' || value.client_name === null
        ? value.client_name
        : undefined,
    status: value.status as string,
    submitted_at: value.submitted_at as string,
    updated_at: value.updated_at as string,
    status_url: value.status_url as string,
    coverage:
      value.coverage &&
      typeof value.coverage === 'object' &&
      !Array.isArray(value.coverage)
        ? (value.coverage as Record<string, unknown>)
        : {},
    limitations: Array.isArray(value.limitations)
      ? value.limitations.filter(
          (item): item is string => typeof item === 'string',
        )
      : [],
    report_url: typeof value.report_url === 'string' ? value.report_url : null,
    delivery: typeof value.delivery === 'string' ? value.delivery : null,
    completion_target:
      typeof value.completion_target === 'string' ||
      value.completion_target === null
        ? value.completion_target
        : undefined,
    target_missed:
      typeof value.target_missed === 'boolean'
        ? value.target_missed
        : undefined,
    completed_late:
      typeof value.completed_late === 'boolean'
        ? value.completed_late
        : undefined,
    working_deadline:
      typeof value.working_deadline === 'string' ||
      value.working_deadline === null
        ? value.working_deadline
        : undefined,
    recovery_eligible:
      typeof value.recovery_eligible === 'boolean'
        ? value.recovery_eligible
        : undefined,
    recovery_blocker:
      typeof value.recovery_blocker === 'string' ||
      value.recovery_blocker === null
        ? value.recovery_blocker
        : undefined,
    recovery_needs_api_key:
      typeof value.recovery_needs_api_key === 'boolean'
        ? value.recovery_needs_api_key
        : undefined,
    recovery_state:
      typeof value.recovery_state === 'string' || value.recovery_state === null
        ? value.recovery_state
        : undefined,
    recovery_mode:
      typeof value.recovery_mode === 'string' || value.recovery_mode === null
        ? value.recovery_mode
        : undefined,
    recovery_attempts:
      typeof value.recovery_attempts === 'number' &&
      Number.isInteger(value.recovery_attempts) &&
      value.recovery_attempts >= 0
        ? value.recovery_attempts
        : undefined,
    recovery_started_at:
      typeof value.recovery_started_at === 'string' ||
      value.recovery_started_at === null
        ? value.recovery_started_at
        : undefined,
  };
}

/** Use Insights token renewal and bounded requests, without service-error retries. */
async function requestConference<T>(
  apiUrl: string,
  authenticatedRequest: RequestWithAuth,
  kind: RequestKind,
  signal: AbortSignal,
  path: string,
  readResponse: (response: Response) => Promise<T>,
  submission?: Submission | Pick<Submission, 'api_key'>,
): Promise<T> {
  const method = kind === 'LIST' ? 'GET' : kind === 'RESUME' ? 'POST' : kind;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort);
  if (signal.aborted) {
    abort();
  }
  // Submission setup has a 120-second budget; recovery validation runs in the background.
  const timeout = setTimeout(abort, kind === 'POST' ? 130_000 : 30_000);
  try {
    const response = await authenticatedRequest(headers =>
      fetch(`${apiUrl.replace(/\/$/, '')}${path}`, {
        method,
        headers: {
          ...headers,
          ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
        },
        body: submission ? JSON.stringify(submission) : undefined,
        signal: controller.signal,
        credentials: 'omit',
        redirect: 'error',
      }),
    );
    return await readResponse(response);
  } catch (error: unknown) {
    if (signal.aborted || error instanceof ConferenceApiError) {
      throw error;
    }
    throw new ConferenceApiError(0, kind);
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
}

/** Submit, resume or delete a report through the existing authenticated adapter. */
export async function conferenceRequest(
  apiUrl: string,
  authenticatedRequest: RequestWithAuth,
  method: Method,
  signal: AbortSignal,
  reportId?: string,
  submission?: Submission | Pick<Submission, 'api_key'>,
): Promise<ReportResponse | undefined> {
  if (method === 'RESUME' && (!reportId || !REPORT_ID_PATTERN.test(reportId))) {
    throw new ConferenceApiError(422, method);
  }
  return requestConference(
    apiUrl,
    authenticatedRequest,
    method,
    signal,
    `/reports${reportId ? `/${reportId}` : ''}${method === 'RESUME' ? '/resume' : ''}`,
    async response => {
      const retry = retryAfterMs(response.headers.get('Retry-After'));
      if (method === 'DELETE' && response.status === 204) {
        return undefined;
      }
      // Only an owned preparing conflict contains a resumable public report.
      if (
        method !== 'DELETE' &&
        (response.ok || (method === 'POST' && response.status === 409))
      ) {
        const report = parseReport(await response.json().catch(() => null));
        if (report && (response.ok || report.status === 'preparing')) {
          return {
            report,
            pending: response.status === 409,
            retryAfterMs: retry,
          };
        }
      }
      throw new ConferenceApiError(response.status, method, retry);
    },
    submission,
  );
}

/** List only backend reports; the collection endpoint is a backend dependency. */
export async function conferenceListRequest(
  apiUrl: string,
  authenticatedRequest: RequestWithAuth,
  signal: AbortSignal,
  cursor?: string,
  status?: string,
): Promise<ReportPage> {
  const params = new URLSearchParams({ limit: '50' });
  if (cursor) {
    params.set('cursor', cursor);
  }
  if (status) {
    params.set('status', status);
  }
  return requestConference(
    apiUrl,
    authenticatedRequest,
    'LIST',
    signal,
    `/reports?${params.toString()}`,
    async response => {
      const retry = retryAfterMs(response.headers.get('Retry-After'));
      if (!response.ok) {
        throw new ConferenceApiError(response.status, 'LIST', retry);
      }
      const body: unknown = await response.json().catch(() => null);
      if (body && typeof body === 'object') {
        const value = body as Record<string, unknown>;
        if (
          Array.isArray(value.items) &&
          (value.next_cursor === null ||
            (typeof value.next_cursor === 'string' &&
              value.next_cursor.length > 0))
        ) {
          const items = value.items.map(parseReport);
          if (items.every((item): item is ConferenceReport => !!item)) {
            return { items, next_cursor: value.next_cursor };
          }
        }
      }
      throw new ConferenceApiError(response.status, 'LIST');
    },
  );
}

/** Keep checking recovery validation even while the lifecycle status is terminal. */
export function isTerminal(report: ConferenceReport): boolean {
  if (report.recovery_state === 'validating' && report.delivery !== 'sent') {
    return false;
  }
  return (
    report.status === 'failed' ||
    (report.status === 'completed' && report.delivery === 'sent') ||
    !['preparing', 'queued', 'running', 'completed'].includes(report.status)
  );
}

/** Only open a backend-provided HTTPS HTML link after confirmed delivery. */
export function reportLink(report: ConferenceReport): string | undefined {
  if (
    report.status !== 'completed' ||
    report.delivery !== 'sent' ||
    !report.report_url
  ) {
    return undefined;
  }
  try {
    const url = new URL(report.report_url);
    return url.protocol === 'https:' ? report.report_url : undefined;
  } catch {
    return undefined;
  }
}
